export interface DocumentSlotDefinition {
  type: string;
  label: string;
  source: 'docuseal' | 'upload' | 'verified' | 'fillable' | 'benefits' | 'generated' | 'preneed';
  regions: ('ontario' | 'quebec')[];
  /** API path for fillable PDFs, e.g. "burial-permit/form" */
  fillableEndpoint?: string;
  /** Download filename prefix for fillable PDFs */
  filenamePrefix?: string;
  /**
   * Form key under funerals.form_confirmations. Slots that set it get a Save
   * button in the PDF editor; the agent's field values are stored as overrides
   * and re-applied by the render endpoint on every subsequent render.
   */
  confirmFormKey?: string;
}

const ALL_SLOTS: DocumentSlotDefinition[] = [
  // Signed Documents (DocuSeal)
  { type: 'cleo_contract', label: 'Cleo Contract', source: 'docuseal', regions: ['ontario', 'quebec'] },
  { type: 'cremation_agreement', label: 'Cremation Agreement', source: 'docuseal', regions: ['ontario', 'quebec'] },
  { type: 'photograph_agreement', label: 'Photograph Agreement', source: 'docuseal', regions: ['ontario', 'quebec'] },

  // Verified Documents
  { type: 'POD', label: 'Proof of Death (POD)', source: 'verified', regions: ['ontario', 'quebec'] },
  { type: 'POC', label: 'Proof of Cremation (POC)', source: 'verified', regions: ['ontario', 'quebec'] },

  // Ontario only. The DocuSeal copy is the signed original / audit record; the
  // correctable version staff submit to the registrar is the fillable slot below.
  { type: 'statement_of_death', label: 'Statement of Death (Signed Original)', source: 'docuseal', regions: ['ontario'] },
  { type: 'medical_certificate', label: 'Medical Certificate', source: 'upload', regions: ['ontario'] },
  // The signed originals that come back to us on paper and get scanned in. Not
  // to be confused with the fillable 'burial_permit' slot below, which is the
  // blank form we generate and send to the registrar.
  { type: 'burial_permit_signed', label: 'Burial Permit (Signed)', source: 'upload', regions: ['ontario'] },
  { type: 'coroner_certificate', label: "Coroner's Certificate", source: 'upload', regions: ['ontario'] },

  // Quebec only
  { type: 'sp3', label: 'SP3', source: 'upload', regions: ['quebec'] },
  { type: 'preneed_confirmation', label: 'Preneed Confirmation', source: 'upload', regions: ['quebec'] },

  // Filled Documents (fillable PDFs pre-populated from funeral data)
  { type: 'statement_of_death_form', label: 'Statement of Death (Form 15)', source: 'fillable', regions: ['ontario'], fillableEndpoint: 'statement-of-death/form', filenamePrefix: 'StatementOfDeath', confirmFormKey: 'statement_of_death' },
  { type: 'toronto_permit_docs', label: 'Toronto Permit Docs', source: 'fillable', regions: ['ontario'], fillableEndpoint: 'burial-permit/toronto-permit-docs', filenamePrefix: 'TorontoPermitDocs', confirmFormKey: 'burial_permit' },
  { type: 'burial_permit', label: 'Burial Permit', source: 'fillable', regions: ['ontario'], fillableEndpoint: 'burial-permit/form', filenamePrefix: 'BurialPermit', confirmFormKey: 'burial_permit' },
  // The signed permit the City sends back after processing our request. Kept
  // distinct from 'burial_permit' above, which is the blank we generate and
  // submit — this one is the only version with legal force.
  { type: 'burial_permit_issued', label: 'Burial Permit (Issued by City)', source: 'generated', regions: ['ontario'] },
  // What Retraite Québec showed after an Envoi numérique send (T-154): our
  // full-page screenshot, and their own "accusé d'envoi" when offered. Private
  // bucket (rrq-submissions) — opened through the backend download route only.
  { type: 'rrq_submission_confirmation', label: 'RRQ Submission Confirmation', source: 'generated', regions: ['quebec'] },
  { type: 'rrq_submission_acknowledgement', label: "RRQ Accusé d'envoi", source: 'generated', regions: ['quebec'] },
  { type: 'survivor_benefits_qpp', label: 'QPP Survivor Benefits', source: 'fillable', regions: ['quebec'], fillableEndpoint: 'survivor-benefits/form', filenamePrefix: 'RRQ_SurvivorBenefits' },

  // Benefits (CPP forms that flow through DocuSeal — Ontario only)
  { type: 'cpp_death_benefit', label: 'CPP Death Benefit', source: 'benefits', regions: ['ontario'] },
  { type: 'cpp_survivor_pension', label: "CPP Survivor's Pension", source: 'benefits', regions: ['ontario'] },

  // Pre-need TruStage flow (T-162): only shown on pre-need cases.
  { type: 'trustage_enrollment', label: 'TruStage enrollment form', source: 'preneed', regions: ['ontario', 'quebec'] },
  { type: 'trustage_certificate', label: 'TruStage certificate', source: 'preneed', regions: ['ontario', 'quebec'] },
  // T-174: the claim form at fruition, signed by the family (QC). Private bucket: it carries the SIN.
  { type: 'trustage_claim_form', label: 'TruStage claim form (signed)', source: 'preneed', regions: ['quebec'] },
  { type: 'preneed_package', label: 'Confirmation package', source: 'preneed', regions: ['ontario', 'quebec'] },

  // Visitation documents
  { type: 'rideau_prep_sheet', label: 'Rideau Prep Sheet', source: 'fillable', regions: ['quebec'], fillableEndpoint: 'prep-sheet', filenamePrefix: 'RideauPrepSheet' },
];

export function getSlotsForRegion(region: string): DocumentSlotDefinition[] {
  const r = region === 'ontario' ? 'ontario' : 'quebec';
  return ALL_SLOTS.filter(slot => slot.regions.includes(r));
}

export function getSlotsBySection(region: string) {
  const slots = getSlotsForRegion(region);
  return {
    signed: slots.filter(s => s.source === 'docuseal'),
    verified: slots.filter(s => s.source === 'verified'),
    uploads: slots.filter(s => s.source === 'upload'),
    fillable: slots.filter(s => s.source === 'fillable'),
    benefits: slots.filter(s => s.source === 'benefits'),
    generated: slots.filter(s => s.source === 'generated'),
    preneed: slots.filter(s => s.source === 'preneed'),
  };
}
